import React, { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import {
  useGenerateStripeUrlMutation,
  useSetStripeAccountConnectedMutation,
} from '@vette/data-access';
import { message } from '@vette/frontend-utils';
import { theme, Title, useWarning } from '@vette/ui-components';
import { Row } from 'antd';

import * as S from './styles';

type Props = {
  viewOnly?: boolean;
  userId?: string;
  stripeAccountConnected?: boolean;
};

export const StripeAccountSettings: React.FC<Props> = ({
  userId,
  stripeAccountConnected,
  viewOnly,
}) => {
  const router = useRouter();
  const warning = useWarning();
  const [generateStripeUrl] = useGenerateStripeUrlMutation();

  const [updateStripeAccountConnected] = useSetStripeAccountConnectedMutation({
    update(cache) {
      cache.evict({ fieldName: 'vetterDashboard' });
    },
  });

  const redirectToStripe = useCallback(async () => {
    if (!userId) {
      return;
    }

    const isSafari = () => navigator.userAgent.includes('Safari');
    const windowReference = isSafari() ? window.open() : null;

    const { data } = await generateStripeUrl({
      variables: {
        userId,
      },
    });

    if (data?.generateStripeUrl?.__typename === 'ValidationError') {
      message.error(data?.generateStripeUrl?.messages);
    }

    if (
      data?.generateStripeUrl?.__typename === 'StripeUrl' &&
      data?.generateStripeUrl?.url
    ) {
      if (windowReference) {
        windowReference.location.href = data.generateStripeUrl.url;
        return;
      } else {
        window.open(data.generateStripeUrl.url);
      }
    }

    if (windowReference) {
      windowReference.close();
    }
  }, [userId, generateStripeUrl]);

  const handleStripeResult = useCallback(async () => {
    const { stripe_result } = router.query ?? {};
    const openConfirmation = () => {
      return warning.openWarning({
        text: ` Fantastic! Your profile setup is complete 🎉`,
        cancelText: 'Close',
        hideOk: true,
      });
    };

    if (!userId || !stripe_result) {
      return;
    }

    if (stripe_result === 'return') {
      await updateStripeAccountConnected({
        variables: {
          userId,
        },
      });

      // remove query params
      router.replace('/payouts', undefined, { shallow: true });
      openConfirmation();
    }

    if (stripe_result === 'refresh') {
      await redirectToStripe();
    }
  }, [router, userId, updateStripeAccountConnected, redirectToStripe, warning]);

  useEffect(() => {
    handleStripeResult();
  }, [handleStripeResult]);

  const tooltipMessage = viewOnly
    ? 'Fill out and save your personal information before proceeding to Stripe account setup.'
    : '';

  return (
    <S.Content>
      <S.Section>
        <S.TitleRow justify="space-between" align="middle">
          <Title level={3}>Account</Title>
        </S.TitleRow>
        <Row justify="space-between" align="middle">
          <S.StripeTitleBox>
            <S.Image src="/images/stripe-logo-small.svg"></S.Image>
            <Title level={4}>Stripe</Title>
          </S.StripeTitleBox>
          <S.StatBadge
            color={
              stripeAccountConnected
                ? theme.colors.green40
                : theme.colors.grey40
            }
          >
            {stripeAccountConnected ? 'CONNECTED' : 'NOT CONNECTED'}
          </S.StatBadge>
        </Row>
        <Row>
          <S.FullWidthFormItem shouldUpdate>
            <S.Button
              actionType="secondary"
              tooltipTitle={tooltipMessage}
              onClick={redirectToStripe}
              disabled={viewOnly}
              block
            >
              {stripeAccountConnected
                ? 'Manage Stripe'
                : 'Set up Stripe account'}
            </S.Button>
            <S.DisclaimerTypography>
              <S.DisclaimerText type="secondary">
                Keep in mind that every transfer of funds to your bank account
                (aka payout) takes 2 business days. You can view the list of all
                of your payouts and the date that you expect to receive them in
                your bank account in the{' '}
                <S.DisclaimerButton
                  type="link"
                  disabled={viewOnly}
                  onClick={redirectToStripe}
                >
                  Stripe Dashboard
                </S.DisclaimerButton>
                .
              </S.DisclaimerText>
            </S.DisclaimerTypography>
          </S.FullWidthFormItem>
        </Row>
      </S.Section>
    </S.Content>
  );
};
